import React, { useEffect, useState, useRef, useCallback } from 'react'
import { Trophy, Zap, Clock, Target, MessageCircle } from 'lucide-react'
import { api } from '@/services/api'
import { getTeamById as getStaticTeamById } from '@/data/teams'
import { useTeamsStore } from '@/stores/teamsStore'
import { fireArcadeEvent } from '@/components/arcade/ArcadeToast'
import GameThread from '@/components/leagues/GameThread'
import clsx from 'clsx'
import { TeamFlairBadge } from '@/components/ui/TeamFlairBadge'
import { FireParticles, UpsetAlert } from '@/components/ui/ArcadeEffects'

/* ── Types ────────────────────────────────────────────────── */
interface SimGame {
  id: string
  round: number
  region: string | null
  team_a_id: string | null
  team_b_id: string | null
  team_a_seed: number | null
  team_b_seed: number | null
  winner_team_id: string | null
  score_a: number | null
  score_b: number | null
  status: string
  simulated_at: string | null
}

interface Picker {
  userId: string
  displayName: string
}

interface FeedEvent {
  id: string
  type: 'game_final' | 'upset'
  game: SimGame
  winnerName: string
  loserName: string
  isUpset: boolean
  roundLabel: string
  timestamp: string
  pickers: Picker[]
}

const ROUND_LABELS: Record<number, string> = {
  1: 'Round 1',
  2: 'Round 2',
  3: 'Sweet 16',
  4: 'Elite 8',
  5: 'Final Four',
  6: 'Championship',
}

/* ── Component ────────────────────────────────────────────── */
interface LiveFeedProps {
  leagueId?: string  // future: filter events by league context
  className?: string
  maxEvents?: number
}

const REACTIONS = ['🔥', '😂', '💀', '🏀', '😤'] as const
type ReactionEmoji = typeof REACTIONS[number]

const LiveFeed: React.FC<LiveFeedProps> = ({ leagueId, className, maxEvents = 30 }) => {
  const [events, setEvents] = useState<FeedEvent[]>([])
  const [loading, setLoading] = useState(true)
  const feedRef = useRef<HTMLDivElement>(null)
  const [newEventId, setNewEventId] = useState<string | null>(null)
  const [reactions, setReactions] = useState<Record<string, Record<string, number>>>({})
  const [myReactions, setMyReactions] = useState<Record<string, string | null>>({})
  const [openThreads, setOpenThreads] = useState<Set<string>>(new Set())
  const storeGetTeam = useTeamsStore(s => s.getTeam)
  const getTeamById = useCallback((id: string) => storeGetTeam(id) ?? getStaticTeamById(id), [storeGetTeam])

  useEffect(() => { useTeamsStore.getState().fetchTeams() }, [])

  const handleReact = async (gameId: string, emoji: ReactionEmoji) => {
    if (!leagueId) return
    // Optimistic update
    const current = myReactions[gameId]
    const removing = current === emoji
    setMyReactions((prev) => ({ ...prev, [gameId]: removing ? null : emoji }))
    setReactions((rc) => {
      const gr = { ...(rc[gameId] || {}) }
      if (current) gr[current] = Math.max(0, (gr[current] || 0) - 1)
      if (!removing) gr[emoji] = (gr[emoji] || 0) + 1
      return { ...rc, [gameId]: gr }
    })
    // Persist
    try {
      await api.post('/sim/react', { gameId, leagueId, emoji })
    } catch (e) {
      console.warn('React error', e)
    }
  }

  const buildEvents = (games: SimGame[], pickersByGame: Record<string, Picker[]> = {}): FeedEvent[] => {
    return games
      .filter((g) => g.status === 'final' && g.winner_team_id && g.team_a_id && g.team_b_id)
      .sort((a, b) => {
        const ta = a.simulated_at ? new Date(a.simulated_at).getTime() : 0
        const tb = b.simulated_at ? new Date(b.simulated_at).getTime() : 0
        return tb - ta // newest first
      })
      .slice(0, maxEvents)
      .map((g) => {
        const winner = g.winner_team_id ? getTeamById(g.winner_team_id) : undefined
        const loserId = g.team_a_id === g.winner_team_id ? g.team_b_id : g.team_a_id
        const loser = loserId ? getTeamById(loserId) : undefined
        const winnerSeed = g.team_a_id === g.winner_team_id ? g.team_a_seed : g.team_b_seed
        const loserSeed = g.team_a_id === g.winner_team_id ? g.team_b_seed : g.team_a_seed
        const isUpset = !!(winnerSeed && loserSeed && winnerSeed > loserSeed)

        return {
          id: g.id,
          type: isUpset ? 'upset' as const : 'game_final' as const,
          game: g,
          winnerName: winner?.shortName || winner?.name || g.winner_team_id || 'TBD',
          loserName: loser?.shortName || loser?.name || loserId || 'TBD',
          isUpset,
          roundLabel: ROUND_LABELS[g.round] || `Round ${g.round}`,
          timestamp: g.simulated_at || '',
          pickers: pickersByGame[g.id] || [],
        }
      })
  }

  const fetchEvents = useCallback(async () => {
    try {
      const url = leagueId ? `/sim/results?leagueId=${leagueId}` : '/sim/results'
      const res = await api.get<{
        games: SimGame[]
        pickersByGame: Record<string, Picker[]>
        reactionsByGame: Record<string, Record<string, number>>
        myReactionsByGame: Record<string, string>
      }>(url)
      const games = res?.games || []
      const pickers = res?.pickersByGame || {}
      console.log('LiveFeed: api returned', games.length, 'games')
      setEvents(games.length > 0 ? buildEvents(games, pickers) : [])
      if (res?.reactionsByGame) setReactions(res.reactionsByGame)
      if (res?.myReactionsByGame) setMyReactions(res.myReactionsByGame)
    } catch (e) {
      console.warn('LiveFeed load error', e)
      setEvents([])
    } finally {
      setLoading(false)
    }
  }, [maxEvents, leagueId])

  // Initial load + poll every 15s + hard timeout failsafe
  useEffect(() => {
    fetchEvents()
    // Failsafe: never show spinner for more than 3s
    const timeout = setTimeout(() => setLoading(false), 3000)
    const interval = setInterval(fetchEvents, 15000)
    return () => { clearTimeout(timeout); clearInterval(interval) }
  }, [fetchEvents])

  // Track previous event count to detect new games and fire arcade toasts
  const prevCountRef = useRef(0)
  useEffect(() => {
    if (events.length > prevCountRef.current && prevCountRef.current > 0) {
      const ev = events[0]
      if (ev) {
        setNewEventId(ev.id)
        if (ev.isUpset) {
          fireArcadeEvent({ type: 'upset', title: 'UPSET!', subtitle: `${ev.winnerName} takes down ${ev.loserName}`, icon: '⚡' })
        } else if (ev.game.round >= 5) {
          fireArcadeEvent({ type: 'points', title: ev.game.round === 6 ? '🏆 CHAMPION' : 'FINAL FOUR', subtitle: `${ev.winnerName} advances!`, icon: ev.game.round === 6 ? '🏆' : '🏀' })
        }
      }
    }
    prevCountRef.current = events.length
  }, [events])

  // Clear "new" highlight after animation
  useEffect(() => {
    if (!newEventId) return
    const t = setTimeout(() => setNewEventId(null), 2000)
    return () => clearTimeout(t)
  }, [newEventId])

  const formatTime = (ts: string) => {
    if (!ts) return ''
    const d = new Date(ts)
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  if (loading) {
    return (
      <div className={clsx('text-center py-12', className)}>
        <Clock className="h-6 w-6 text-white/10 animate-pulse mx-auto mb-2" />
        <p className="text-xs text-white/20">Loading live feed…</p>
      </div>
    )
  }

  if (events.length === 0) {
    return (
      <div className={clsx('text-center py-12', className)}>
        <Zap className="h-8 w-8 text-white/10 mx-auto mb-3" />
        <h3 className="text-sm font-semibold text-white mb-1">No results yet</h3>
        <p className="text-xs text-white/20">Game results will appear here as they happen.</p>
      </div>
    )
  }

  return (
    <div className={clsx('space-y-2', className)} ref={feedRef}>
      {events.map((event) => (
        <div
          key={event.id}
          className={clsx(
            'p-3 rounded-xl border transition-all duration-500',
            event.isUpset
              ? 'bg-orange-500/[0.06] border-orange-500/20 shadow-[0_0_20px_rgba(251,146,60,0.08)]'
              : 'bg-white/[0.02] border-white/[0.06]',
            event.isUpset && 'animate-[glow_2s_ease-in-out_infinite]',
            event.id === newEventId && 'animate-[slideIn_0.4s_ease-out] ring-2 ring-orange-400/40',
          )}
          style={event.id === newEventId ? { animationFillMode: 'both' } : undefined}
        >
          {/* Fire particles on upsets */}
          {event.isUpset && <FireParticles intensity={3} />}

          <div className="flex items-start gap-3">
            {/* Icon */}
            <div className={clsx(
              'w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-all duration-300',
              event.isUpset ? 'bg-gradient-to-br from-orange-500/30 to-red-500/20 shadow-[0_0_12px_rgba(251,146,60,0.2)]' :
              event.game.round >= 5 ? 'bg-gradient-to-br from-amber-500/20 to-yellow-500/10' :
              'bg-white/[0.06]',
            )}>
              {event.isUpset ? (
                <Zap className="h-5 w-5 text-orange-400 drop-shadow-[0_0_6px_rgba(251,146,60,0.5)]" />
              ) : event.game.round >= 5 ? (
                <Trophy className="h-5 w-5 text-amber-400 drop-shadow-[0_0_6px_rgba(245,158,11,0.5)]" />
              ) : (
                <Target className="h-4 w-4 text-white/30" />
              )}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-xs font-medium text-white/50">{event.roundLabel}</span>
                {event.isUpset && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gradient-to-r from-orange-500/30 to-red-500/20 text-orange-300 font-bold tracking-wide shadow-[0_0_8px_rgba(251,146,60,0.2)]">
                    ⚡ UPSET
                  </span>
                )}
                {event.game.round >= 5 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gradient-to-r from-amber-500/30 to-yellow-500/20 text-amber-300 font-bold tracking-wide">
                    {event.game.round === 6 ? '🏆 CHAMPIONSHIP' : '🏀 FINAL FOUR'}
                  </span>
                )}
              </div>
              <div className="text-sm text-white font-medium">
                <span className={clsx(
                  'text-green-400 font-semibold',
                  event.isUpset && 'bg-gradient-to-r from-orange-400 via-yellow-300 to-orange-400 bg-clip-text text-transparent bg-[length:200%] animate-[shimmer_2s_linear_infinite]',
                )}>{event.winnerName}</span>
                {event.game.score_a != null && event.game.score_b != null && (
                  <span className={clsx(
                    'mx-1.5 font-bold tabular-nums',
                    event.id === newEventId ? 'text-white animate-[punch_0.3s_ease-out]' : 'text-white/40',
                  )}>
                    {event.game.team_a_id === event.game.winner_team_id
                      ? `${event.game.score_a}–${event.game.score_b}`
                      : `${event.game.score_b}–${event.game.score_a}`}
                  </span>
                )}
                <span className="text-white/30">def.</span>
                <span className="text-white/40 ml-1.5">{event.loserName}</span>
              </div>
            </div>

            {/* Time */}
            <span className="text-[10px] text-white/15 shrink-0">{formatTime(event.timestamp)}</span>
          </div>

          {/* Who picked this? */}
          <div className="mt-2 pt-2 border-t border-white/[0.04] flex items-center gap-1.5 flex-wrap">
            {event.pickers.length > 0 ? (
              <>
                <span className="text-[10px] text-green-400/60">✅</span>
                {event.pickers.slice(0, 5).map((p) => (
                  <span key={p.userId} className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-500/10 text-green-400/70 font-medium inline-flex items-center gap-1">
                    <TeamFlairBadge teamId={(p as any).favoriteTeamId} size="xs" />
                    {p.displayName}
                  </span>
                ))}
                {event.pickers.length > 5 && (
                  <span className="text-[10px] text-white/20">+{event.pickers.length - 5} more</span>
                )}
              </>
            ) : (
              <span className="text-[10px] text-white/15 italic">No one in your league picked this winner</span>
            )}
          </div>

          {/* Reaction bar + chat toggle */}
          <div className={clsx(
            'mt-2 pt-2 flex items-center gap-1',
            event.pickers.length === 0 && 'border-t border-white/[0.04]',
          )}>
            {REACTIONS.map((emoji) => {
              const count = reactions[event.id]?.[emoji] || 0
              const isActive = myReactions[event.id] === emoji
              return (
                <button
                  key={emoji}
                  onClick={() => handleReact(event.id, emoji)}
                  className={clsx(
                    'flex items-center gap-1 px-2.5 py-1.5 rounded-full text-sm transition-all duration-300 active:scale-90',
                    isActive
                      ? 'bg-orange-500/20 ring-1 ring-orange-400/40 shadow-[0_0_8px_rgba(251,146,60,0.3)]'
                      : 'bg-white/[0.04] hover:bg-white/[0.10] hover:scale-105',
                    count > 0 ? 'opacity-100' : 'opacity-50 hover:opacity-80',
                  )}
                >
                  <span className={clsx(
                    'transition-transform duration-300',
                    isActive && 'scale-[1.3] animate-bounce',
                  )}>{emoji}</span>
                  {count > 0 && <span className="text-[11px] font-semibold text-white/60 ml-0.5">{count}</span>}
                </button>
              )
            })}

            {/* Chat toggle */}
            {leagueId && (
              <button
                onClick={() => setOpenThreads((prev) => {
                  const next = new Set(prev)
                  if (next.has(event.id)) next.delete(event.id)
                  else next.add(event.id)
                  return next
                })}
                className={clsx(
                  'ml-auto flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs transition-all duration-200',
                  openThreads.has(event.id)
                    ? 'bg-blue-500/20 text-blue-400'
                    : 'bg-white/[0.04] text-white/30 hover:bg-white/[0.08] hover:text-white/50',
                )}
              >
                <MessageCircle className="h-3.5 w-3.5" />
                <span className="text-[10px]">Chat</span>
              </button>
            )}
          </div>

          {/* Inline game thread */}
          {leagueId && openThreads.has(event.id) && (
            <div className="mt-2">
              <GameThread leagueId={leagueId} gameId={event.id} />
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

export default LiveFeed
